import React, { useState, useRef, useEffect } from 'react';
import { logEvent } from '../services/telemetryService';
import { RadarChartComponent } from './charts/RadarChart';
import { HEAD_AXES, SHAFT_AXES, IRON_HEAD_AXES, IRON_SHAFT_AXES } from '../config/radarAxes';
import { loadRadarPreferences } from '../services/recommendationService';

// ── Feature flag — set to false to hide Radar Match tab without removing code ──
const RADAR_MATCH_TAB_ENABLED = true;

// Type definitions for structured recommendations (supports both ML and rule-based modes)
interface ShaftRecommendation {
  shaft_id: number;
  shaft_brand: string;
  shaft_model: string;
  shaft_flex: string;
  shaft_weight: number;
  predicted_rating?: number;       // Combined model score (legacy)
  head_score?: number;              // Stage 1 / Head score
  shaft_score?: number;             // Stage 2 / Shaft score
  combined_score?: number;          // Final combined score
  // Rule-based extras
  base_combined?: number;
  penalty_multiplier?: number;
  penalty_reasons?: string;
  predicted_spin?: number;
  explanation?: string;
  easy_to_hit_score?: number;
  // V5.2 new fields
  launch_optimization_score?: number;
  consistency_fit_score?: number;
  player_consistency?: number;
  // Detailed breakdown
  moi_score?: number;
  tech_score?: number;
  loft_score?: number;
  flex_score?: number;
  weight_score?: number;
  spin_optimization_score?: number;
  player_fit_score?: number;
  synergy_score?: number;
  launch_complement_score?: number;
  stability_fit_score?: number;
  score_reasons?: Array<{
    key: string;
    score: number;
    label: string;
    detail: string;
    flag: 'good' | 'ok' | 'warn';
  }>;
  combo_radar?: {
    head_radar: Record<string, number>;
    shaft_radar: Record<string, number>;
  };
  iron_diagnosis?: IronDiagnosis;
  // Step 6A: per-shaft combo trajectory (trajectory_level='combo')
  combo_trajectory?: HeadTrajectory | null;
  // Sitting 1: per-(head, shaft) availability + price (display/filter only, not a scoring input)
  shaft_availability?: ShaftAvailability;
}

interface ShaftAvailability {
  fit_tier: 'stock_standard' | 'stock_alternate' | 'custom_upcharge' | string | null;
  upcharge_usd: number | null;
  price_source: 'junction' | 'no_upcharge_on_file' | 'estimate' | 'unknown';
}

interface IronDiagnosis {
  has_problems: boolean;
  warn_count: number;
  culprit_summary: string;
  active_problems: string[];
}

interface DriverDiagnosis {
  measured_spin: number;
  optimal_spin: number;
  total_excess_rpm: number;
  head_spin_effect: number;
  shaft_spin_effect: number;
  equip_fixable_rpm: number;
  swing_excess_rpm: number;
  primary_cause: 'head' | 'shaft' | 'swing' | 'balanced';
  spin_problem: 'high' | 'low' | 'ok';
  culprit_summary: string;
}

interface StockShaft {
  shaft_id?: number;
  shaft_brand?: string;
  shaft_model?: string;
  flex: string;
  weight: number | null;
  // Absent when this is the averaged-top-3 placeholder (no club_stock_shafts row for this
  // head) — irons fall back to that placeholder; drivers always have a real junction row.
  fit_tier?: 'stock_standard' | 'stock_alternate';
  upcharge_usd?: number | null;
  description: string;
}

// V9 trajectory channel (per candidate head; display/telemetry only)
interface HeadTrajectory {
  carry_gain_yds?: number | null;
  consistency_gain_yds?: number | null;
  estimated_mishit_loss_yds?: number | null;
  confidence?: 'high' | 'medium' | null;
  // Step 6A combo fields (trajectory_level='combo')
  trajectory_level?: 'head' | 'combo' | 'measured_current' | null;
  shaft_spin_effect?: number | null;
  shaft_launch_effect?: number | null;
  shaft_confidence?: 'high' | 'medium' | null;
  current_shaft_source?: string | null;
  predicted_spin_rpm?: number | null;
  predicted_launch_deg?: number | null;
  predicted_ball_speed?: number | null;
}

interface HeadRecommendation {
  head_id: number;
  head_brand: string;
  head_model: string;
  head_loft: number;
  head_year?: number;
  head_score: number;
  head_tags?: string[];
  trajectory?: HeadTrajectory | null;
  trajectory_head_only?: HeadTrajectory | null;
  shafts: ShaftRecommendation[];
  stock_shaft: StockShaft | null;
}

// V9 measured-current benchmark (metadata.current_driver_benchmark)
interface CurrentDriverBenchmark {
  brand?: string | null;
  model?: string | null;
  status?: 'already_well_fit' | 'upgrade_likely' | 'no_clear_upgrade' | 'room_to_improve';
  top_candidate_margin_yds?: number;
  within_optimal_yds?: number;
  estimated_mishit_loss_yds?: number | null;
  current_head_year?: number | null;
  current_head_confidence?: 'high' | 'medium' | 'low' | null;
}

// ── V9 types ────────────────────────────────────────────────────────────────
interface PlayerNeed {
  category: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  direction: 'INCREASE' | 'REDUCE' | 'NONE';
  delta: number;
  confidence: number;
  human_label: string;
  equipment_hint: string;
}

interface ApproachCombo {
  head: { head_brand: string; head_model: string; head_loft: number; head_score: number };
  shaft: { shaft_brand: string; shaft_model: string; shaft_flex: string; shaft_weight: number; shaft_score: number };
  combined_score: number;
}

interface ApproachPath {
  label: string;
  rationale: string;
  archetype_key: string;
  primary_need: string;
  top_combos: ApproachCombo[];
}

interface MLRecommendationsData {
  heads: HeadRecommendation[];
  driver_diagnosis?: DriverDiagnosis;
  player_needs?: { needs: PlayerNeed[]; profile_completeness: number };
  approaches?: ApproachPath[];
  metadata: {
    profile_id: number;
    total_heads: number;
    shafts_per_head: number;
    filtering_mode?: string;
    scoring_mode?: 'ml' | 'rule_based' | 'constraint_first';
    scoring_version?: string;
    total_combos_evaluated?: number;
    archetype_source?: string;
    archetype_label?: string;
    v9_trajectory_active?: boolean;
    current_driver_benchmark?: CurrentDriverBenchmark | null;
  };
  status: string;
  equipment_type: string;
}

interface MLRecommendationResultsProps {
  data: MLRecommendationsData | null;
  loading?: boolean;
  error?: string | null;
  onBack?: () => void;
  className?: string;
  sessionId?: string;
}

const MAX_SELECTED = 5;

// ── Tier classification ────────────────────────────────────────────────────
type TierNumber = 1 | 2 | 3 | 4;

function classifyTier(score: number, bestScore?: number): TierNumber {
  // Dynamic tiers: relative to the best head score in the set.
  // This ensures meaningful tier splits regardless of absolute score range.
  const top = bestScore ?? 9.0;
  if (score >= top - 0.3) return 1;   // within 0.3 of best → "Best for You"
  if (score >= top - 0.8) return 2;   // within 0.8 → "Good Match"
  if (score >= top - 1.3) return 3;   // within 1.3 → "Acceptable"
  return 4;                            // more than 1.3 below best → "Not Suited"
}

function getTierMeta(tier: TierNumber) {
  return ({
    1: { label: 'Best for You', badge: 'bg-amber-100 text-amber-800 border-amber-300', border: 'border-amber-400', headerBg: 'bg-amber-50' },
    2: { label: 'Good Match',   badge: 'bg-green/15 text-green-ink border-green-ink', border: 'border-green-ink', headerBg: 'bg-green/10' },
    3: { label: 'Acceptable',   badge: 'bg-gray-100 text-gray-600 border-gray-300',    border: 'border-gray-200',  headerBg: 'bg-gray-50'  },
    4: { label: 'Not Suited',   badge: '',                                              border: '',                 headerBg: ''            },
  } as const)[tier];
}

function getTier4Reason(head: HeadRecommendation): string {
  const tags = head.head_tags ?? [];
  if (tags.includes('Low Spin') || tags.includes('High Launch') || tags.includes('Max Launch')) {
    return 'spin/launch profile not matched to your swing';
  }
  if (head.head_year && head.head_year < 2020) {
    return 'older model — technology gap vs current offerings';
  }
  return 'fit score below threshold for your profile';
}

const MLRecommendationResults: React.FC<MLRecommendationResultsProps> = ({
  data,
  loading = false,
  error = null,
  onBack,
  className = "",
  sessionId = 'unknown',
}) => {
  // Progressive display state (for 'all' mode)

  // Head selection state
  const [selectedHeadIds, setSelectedHeadIds] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<'all' | 'selected'>('all');

  // Sitting 3/4: shaft-availability filter — display/query-time only, never wired into scoring
  type ShaftFilterMode = 'all' | 'stock' | 'stock_alternate' | 'small_upcharge';
  const [shaftFilterMode, setShaftFilterMode] = useState<ShaftFilterMode>('all');

  // Head-level expansion state — expanding one shaft expands all for that head
  const [expandedHeads, setExpandedHeads] = useState<Set<number>>(new Set());

  // Head-level explanation collapse state — first head expanded by default
  const [collapsedExplanations, setCollapsedExplanations] = useState<Set<number>>(new Set());

  // Telemetry: track when each head was expanded so we can compute time_open_seconds
  const expandOpenTimeRef = useRef<Map<number, number>>(new Map());

  // Tier 3 expand state (collapsed by default)
  const [tier3Open, setTier3Open] = useState(false);

  // ── Fitting Analysis collapse state ──────────────────────────────────────
  const [fittingAnalysisOpen, setFittingAnalysisOpen] = useState(true);

  // ── Approach path tab state ───────────────────────────────────────────────
  // null = "Best Fit" tab; number = index into approaches array
  const [activeApproachIdx, setActiveApproachIdx] = useState<number | null>(null);

  // ── Radar Match tab state ─────────────────────────────────────────────────
  type SortMode = 'bestfit' | 'radarMatch';
  const [sortMode, setSortMode] = useState<SortMode>('bestfit');
  const [radarPrefs, setRadarPrefs] = useState<{
    head: Record<string, number>;
    shaft: Record<string, number>;
  } | null>(null);
  const [radarPrefsLoaded, setRadarPrefsLoaded] = useState(false);

  // Pick correct axes based on equipment type
  const isIron = data?.equipment_type !== 'driver';
  const activeHeadAxes = isIron ? IRON_HEAD_AXES : HEAD_AXES;
  const activeShaftAxes = isIron ? IRON_SHAFT_AXES : SHAFT_AXES;

  // Load radar preferences for both drivers and irons
  useEffect(() => {
    if (!RADAR_MATCH_TAB_ENABLED) return;
    const eqType = data?.equipment_type;
    if (!eqType || (eqType !== 'driver' && eqType !== 'iron')) return;
    const profileId = data?.metadata?.profile_id;
    if (!profileId) return;
    loadRadarPreferences(profileId, eqType)
      .then(prefs => { setRadarPrefs(prefs); setRadarPrefsLoaded(true); })
      .catch(() => setRadarPrefsLoaded(true));
  }, [data?.metadata?.profile_id, data?.equipment_type]);

  /** Score 0–1: how closely a shaft's combo_radar matches user's slider preferences. */
  const computePreferenceScore = (shaft: ShaftRecommendation): number => {
    if (!shaft.combo_radar || !radarPrefs) return 0;
    const headKeys = activeHeadAxes.map(a => a.key);
    const shaftKeys = activeShaftAxes.map(a => a.key);
    const headScore = headKeys.reduce((sum, k) => {
      const val = shaft.combo_radar!.head_radar[k] ?? 5;
      const pref = radarPrefs.head[k] ?? 5;
      return sum + (1 - Math.abs(val - pref) / 9);
    }, 0) / headKeys.length;
    const shaftScore = shaftKeys.reduce((sum, k) => {
      const val = shaft.combo_radar!.shaft_radar[k] ?? 5;
      const pref = radarPrefs.shaft[k] ?? 5;
      return sum + (1 - Math.abs(val - pref) / 9);
    }, 0) / shaftKeys.length;
    return (headScore + shaftScore) / 2;
  };

  /** Return heads re-sorted by radar preference score (shafts re-sorted within each head). */
  const getRadarMatchHeads = (): HeadRecommendation[] => {
    if (!data || !radarPrefs) return data?.heads ?? [];
    return [...data.heads]
      .map(head => ({
        ...head,
        shafts: [...head.shafts].sort((a, b) => computePreferenceScore(b) - computePreferenceScore(a)),
      }))
      .sort((a, b) => computePreferenceScore(b.shafts[0]) - computePreferenceScore(a.shafts[0]));
  };

  const showRadarMatchTab =
    RADAR_MATCH_TAB_ENABLED &&
    (data?.equipment_type === 'driver' || data?.equipment_type === 'iron') &&
    radarPrefsLoaded;

  const toggleHeadSelection = (headId: number) => {
    setSelectedHeadIds(prev => {
      const next = new Set(prev);
      if (next.has(headId)) {
        next.delete(headId);
      } else if (next.size < MAX_SELECTED) {
        next.add(headId);
      }
      return next;
    });
  };

  const toggleHeadExpand = (headId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedHeads(prev => {
      const next = new Set(prev);
      const isCurrentlyOpen = next.has(headId);
      const head = data?.heads.find(h => h.head_id === headId);
      const rank = data ? data.heads.findIndex(h => h.head_id === headId) + 1 : 0;
      const equipmentName = head ? `${head.head_brand} ${head.head_model}` : String(headId);
      const clubCategory = data?.equipment_type ?? 'unknown';

      if (isCurrentlyOpen) {
        // Collapsing — compute time open
        const openedAt = expandOpenTimeRef.current.get(headId);
        const timeOpen = openedAt ? (Date.now() - openedAt) / 1000 : undefined;
        expandOpenTimeRef.current.delete(headId);
        logEvent({
          session_id: sessionId,
          event_type: 'result_card_collapsed',
          metadata: { club_category: clubCategory, rank, equipment_name: equipmentName, time_open_seconds: timeOpen },
        });
        next.delete(headId);
      } else {
        // Expanding
        expandOpenTimeRef.current.set(headId, Date.now());
        logEvent({
          session_id: sessionId,
          event_type: 'result_card_expanded',
          metadata: { club_category: clubCategory, rank, equipment_name: equipmentName },
        });
        next.add(headId);
      }
      return next;
    });
  };

  const toggleExplanationCollapse = (headId: number) => {
    setCollapsedExplanations(prev => {
      const next = new Set(prev);
      if (next.has(headId)) next.delete(headId);
      else next.add(headId);
      return next;
    });
  };

  const handleCompareSelected = () => {
    setViewMode('selected');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const selectedHeads = data?.heads.filter(h => selectedHeadIds.has(h.head_id)) ?? [];
    logEvent({
      session_id: sessionId,
      event_type: 'shaft_options_requested',
      metadata: {
        club_category: data?.equipment_type ?? 'unknown',
        head_count: selectedHeads.length,
        head_names: selectedHeads.map(h => `${h.head_brand} ${h.head_model}`),
      },
    });
  };

  const handleBackToAll = () => {
    setViewMode('all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearSelection = () => {
    setSelectedHeadIds(new Set());
    setViewMode('all');
  };


  // ── Explanation helpers ───────────────────────────────────────────────────

  // Regex to match terms that should be bolded in explanations
  const BOLD_TERMS_REGEX = new RegExp(
    [
      '\\b\\d+\\s*mph\\b',           // swing speed: "105 mph"
      '\\b\\d+g\\b',                 // shaft weight: "65g"
      '\\d+(?:\\.\\d+)?°',           // loft: "10.5°"
      '\\b\\d[,\\d]*\\s*rpm\\b',     // spin: "2,350 rpm"
      '\\bvery high MOI\\b',
      '\\bhigh MOI\\b',
      '\\bextremely forgiving\\b',
      '\\bvery forgiving\\b',
      '\\bstrong forgiveness\\b',
      '\\bforgiving\\b',
      '\\bextra stiff\\b',
      '\\bTour X\\b',
      '\\bstiff\\b',
      '\\bregular flex\\b',
      '\\bsenior flex\\b',
      '\\bladies flex\\b',
      '\\blow-spin\\b',
      '\\bworkable\\b',
      '\\boptimal\\b',
    ].join('|'),
    'gi'
  );

  /** Split a text string into segments, wrapping bold-matched terms in <strong>. */
  const highlightText = (text: string): React.ReactNode => {
    const captureRegex = new RegExp(`(${BOLD_TERMS_REGEX.source})`, 'gi');
    const parts = text.split(captureRegex);
    return (
      <>
        {parts.map((part, i) =>
          i % 2 === 1 && part
            ? <strong key={i} className="font-semibold text-gray-800">{part}</strong>
            : <React.Fragment key={i}>{part}</React.Fragment>
        )}
      </>
    );
  };

  /** Label + colour for easy-to-hit badge based on score (1–10). Returns null if score is missing. */
  const getEasyToHitInfo = (score?: number): { label: string; color: string } | null => {
    if (score === undefined || score === null) return null;
    if (score > 7) return { label: 'Very Easy to Hit', color: 'text-green-ink' };
    if (score > 5) return { label: 'Easy to Hit', color: 'text-green-ink' };
    return { label: 'Slightly harder to hit, but suits your needs', color: 'text-amber-600' };
  };

  /** Badge label + style for a shaft's per-head availability/price. Returns null when there's nothing on file. */
  const getAvailabilityBadge = (av?: ShaftAvailability): { label: string; className: string } | null => {
    if (!av || av.price_source === 'unknown') return null;
    const stockClass = 'bg-green/15 text-green-ink border-green-ink';
    const upchargeClass = 'bg-amber-50 text-amber-700 border-amber-200';
    const unknownClass = 'bg-gray-100 text-gray-500 border-gray-300';
    if (av.price_source === 'no_upcharge_on_file') {
      return { label: 'No upcharge on file', className: unknownClass };
    }
    if (av.price_source === 'junction') {
      if (!av.upcharge_usd) return { label: 'Stock', className: stockClass };
      return { label: `+$${av.upcharge_usd.toFixed(0)} upcharge`, className: upchargeClass };
    }
    // price_source === 'estimate' — shafts-table rollup, not a sourced per-head fact
    if (av.upcharge_usd != null) {
      return { label: `+$${av.upcharge_usd.toFixed(0)} est. street price`, className: upchargeClass };
    }
    return null;
  };

  /**
   * Bucket a shaft for the 3-way availability filter. `null` means "no specific bucket" —
   * still visible under 'all', just not called out by any pill (this is where genuinely
   * premium-priced shafts land — no dedicated "Premium" pill since they show up under 'all'
   * anyway). Threshold for "small" upcharge differs by club type: irons top out much lower
   * than drivers on this catalog (S2's iron price-band work froze the iron premium line at
   * $100 street price; $20 upcharge here is the fitting-side "small" cutoff, not that line).
   */
  const getShaftFilterBucket = (shaft: ShaftRecommendation): Exclude<ShaftFilterMode, 'all'> | null => {
    const av = shaft.shaft_availability;
    if (!av || av.fit_tier == null) return null;
    const smallUpchargeMax = data?.equipment_type === 'iron' ? 20 : 200;

    if (av.fit_tier === 'stock_standard') return 'stock';

    if (av.fit_tier === 'stock_alternate') {
      if (av.upcharge_usd != null && av.upcharge_usd > 0) {
        return av.upcharge_usd <= smallUpchargeMax ? 'small_upcharge' : null;
      }
      return 'stock_alternate';
    }

    if (av.fit_tier === 'custom_upcharge') {
      if (av.upcharge_usd != null && av.upcharge_usd > 0 && av.upcharge_usd <= smallUpchargeMax) {
        return 'small_upcharge';
      }
      return null;
    }

    // Estimate fallback (shafts.availability_tier) — only 'stock'/'easily_available' map to a
    // pill; 'available_premium'/'hard_to_find' have no per-head upcharge to bucket by amount.
    if (av.fit_tier === 'stock' || av.fit_tier === 'easily_available') return 'stock';
    return null;
  };

  /** Extract the head-level explanation (sentence 1) and shaft-specific sentences from explanation text. */
  const splitExplanation = (explanation: string): { headSentence: string; shaftSentences: string[] } => {
    const sentences = explanation.split(/(?<=\.)\s+/).filter(s => s.trim());
    return {
      headSentence: sentences[0] || '',
      shaftSentences: sentences.slice(1),
    };
  };


  // Which heads to render
  const orderedHeads = sortMode === 'radarMatch' ? getRadarMatchHeads() : (data?.heads ?? []);
  const headsToShow: HeadRecommendation[] = viewMode === 'selected'
    ? (orderedHeads.filter(h => selectedHeadIds.has(h.head_id)) || [])
    : orderedHeads;

  // How many shafts to show per head — filter (on the full per-head list) before truncating,
  // so a client-side filter never hides options that were already cut by num_shafts_per_head.
  const getShaftsToShow = (head: HeadRecommendation): ShaftRecommendation[] => {
    const filtered = shaftFilterMode === 'all'
      ? head.shafts
      : head.shafts.filter(s => getShaftFilterBucket(s) === shaftFilterMode);
    return viewMode === 'selected' ? filtered : filtered.slice(0, 3);
  };

  // ── Shaft card renderer ──────────────────────────────────────────────────
  const renderShaftCard = (shaft: ShaftRecommendation, head: HeadRecommendation, shaftIndex: number) => {
    const isExpanded = expandedHeads.has(head.head_id);
    const primaryScore = shaft.combined_score ?? shaft.predicted_rating ?? 0;
    const has3StageScores = shaft.head_score !== undefined && shaft.shaft_score !== undefined;
    const hasRuleBasedExtras = shaft.base_combined !== undefined && shaft.penalty_multiplier !== undefined;
    const prefScore = sortMode === 'radarMatch' && radarPrefs ? computePreferenceScore(shaft) : null;

    // Shaft-specific explanation sentences (skip first sentence — it's the head description shown above)
    const shaftSentences = shaft.explanation
      ? splitExplanation(shaft.explanation).shaftSentences
      : [];

    return (
      <div
        key={shaft.shaft_id}
        className="border border-gray-200 rounded-lg bg-white hover:border-green-ink hover:shadow-sm transition-all"
      >
        {/* Compact header — always visible, click to expand all shafts for this head */}
        <button
          className="w-full text-left px-3 py-2 flex items-center gap-2"
          onClick={(e) => toggleHeadExpand(head.head_id, e)}
          aria-expanded={isExpanded}
        >
          {shaftIndex === 0 && sortMode !== 'radarMatch' && (
            <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded shrink-0">
              TOP
            </span>
          )}
          {shaftIndex === 0 && sortMode === 'radarMatch' && (
            <span className="text-xs font-semibold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded shrink-0">
              BEST MATCH
            </span>
          )}
          {prefScore !== null && (
            <span className="text-[10px] font-medium text-purple-600 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded shrink-0">
              {Math.round(prefScore * 100)}% pref
            </span>
          )}
          {(() => {
            const driverDiag = data?.driver_diagnosis;
            if (!driverDiag || driverDiag.spin_problem === 'ok' || !driverDiag.equip_fixable_rpm || !shaft.predicted_spin) return null;
            const gapClosed = Math.round((driverDiag.measured_spin - shaft.predicted_spin) / Math.abs(driverDiag.equip_fixable_rpm) * 100);
            if (gapClosed <= 0) return null;
            const pct = Math.min(gapClosed, 100);
            return (
              <span className="text-[10px] font-medium text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded shrink-0">
                fixes ~{pct}% spin
              </span>
            );
          })()}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-900 truncate leading-tight">
              {shaft.shaft_brand} {shaft.shaft_model}
            </p>
            <p className="text-xs text-gray-500 leading-tight">
              {shaft.shaft_flex} • {shaft.shaft_weight}g
              {(() => {
                const badge = getAvailabilityBadge(shaft.shaft_availability);
                if (!badge) return null;
                return (
                  <span className={`ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium ${badge.className}`}>
                    {badge.label}
                  </span>
                );
              })()}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <div className="w-14 bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-green-ink h-1.5 rounded-full"
                style={{ width: `${Math.min((primaryScore / 10) * 100, 100)}%` }}
              />
            </div>
            <span className="text-xs font-bold text-gray-700 w-7 text-right">
              {primaryScore.toFixed(1)}
            </span>
            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs shrink-0 transition-colors ${isExpanded ? 'bg-green/15 text-green-ink' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
              <span className="text-[10px] font-medium">{isExpanded ? 'Less' : 'Details'}</span>
              <svg
                className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </div>
        </button>

        {/* Shaft-specific explanation bullets (only sentences 2+, since sentence 1 is shown at head level) */}
        {shaftSentences.length > 0 && (
          <div className="px-3 pb-2 border-t border-gray-50 pt-1.5">
            <ul className="space-y-0.5">
              {shaftSentences.map((sentence, i) => (
                <li key={i} className="flex gap-1.5 text-xs text-gray-600 leading-snug">
                  <span className="text-green-ink shrink-0 mt-px">•</span>
                  <span>{highlightText(sentence.replace(/\.$/, ''))}</span>
                </li>
              ))}
              {/* Easy-to-hit bullet */}
              {(() => {
                const easyInfo = getEasyToHitInfo(shaft.easy_to_hit_score);
                if (!easyInfo) return null;
                return (
                  <li className="flex gap-1.5 text-xs leading-snug">
                    <span className="text-green-ink shrink-0 mt-px">•</span>
                    <span className={`font-semibold ${easyInfo.color}`}>{easyInfo.label}</span>
                  </li>
                );
              })()}
            </ul>
          </div>
        )}

        {/* Expanded technical details */}
        {isExpanded && (
          <div className="px-3 pb-3 border-t border-gray-100 pt-2 space-y-2">
            {has3StageScores && (
              <>
                {/* Score summary row */}
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>Head: <span className="font-medium text-gray-700">{shaft.head_score?.toFixed(1)}</span></span>
                  {shaft.player_fit_score !== undefined && (
                    <span>Fit: <span className="font-medium text-gray-700">{shaft.player_fit_score.toFixed(1)}</span></span>
                  )}
                  {shaft.synergy_score !== undefined && (
                    <span>Synergy: <span className="font-medium text-gray-700">{shaft.synergy_score.toFixed(1)}</span></span>
                  )}
                  {hasRuleBasedExtras && shaft.penalty_multiplier! < 1 && (
                    <span className="text-red-600 font-medium">
                      -{((1 - shaft.penalty_multiplier!) * 100).toFixed(0)}% penalty
                    </span>
                  )}
                </div>

                {/* Score reasons — visual bar breakdown */}
                {shaft.score_reasons && shaft.score_reasons.length > 0 && (
                  <div className="space-y-1">
                    {shaft.score_reasons.map((r) => {
                      const flagColor = r.flag === 'good' ? '#16a34a' : r.flag === 'ok' ? '#ca8a04' : '#dc2626';
                      const bgColor = r.flag === 'good' ? '#dcfce7' : r.flag === 'ok' ? '#fef9c3' : '#fee2e2';
                      return (
                        <div key={r.key} className="flex items-center gap-2">
                          <div className="w-28 shrink-0">
                            <div className="flex items-center gap-1">
                              <div className="w-16 bg-gray-100 rounded-full h-1.5 shrink-0">
                                <div
                                  className="h-1.5 rounded-full transition-all"
                                  style={{ width: `${(r.score / 10) * 100}%`, backgroundColor: flagColor }}
                                />
                              </div>
                              <span className="text-[10px] font-bold w-6 text-right" style={{ color: flagColor }}>
                                {r.score.toFixed(1)}
                              </span>
                            </div>
                          </div>
                          <div className="min-w-0 flex items-baseline gap-1.5 flex-wrap">
                            <span
                              className="text-[10px] font-semibold px-1 py-0.5 rounded shrink-0"
                              style={{ backgroundColor: bgColor, color: flagColor }}
                            >
                              {r.label}
                            </span>
                            <span className="text-[10px] text-gray-500 leading-tight truncate">{r.detail}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Penalty reasons */}
                {shaft.penalty_reasons && (
                  <div className="text-xs text-red-600 space-y-0.5 pt-1 border-t border-gray-100">
                    {shaft.penalty_reasons.split('|').map((reason, i) => (
                      <div key={i}>• {reason.trim()}</div>
                    ))}
                  </div>
                )}

                {/* Combo radar — recommended (blue) + user preferences (orange) */}
                {shaft.combo_radar && (() => {
                  let savedPrefs: { head?: Record<string, number>; shaft?: Record<string, number> } = {};
                  try {
                    savedPrefs = JSON.parse(localStorage.getItem(isIron ? 'equipment_preferences_iron' : 'equipment_preferences') || '{}');
                  } catch { /* ignore */ }
                  const headPrefs = savedPrefs.head;
                  const shaftPrefs = savedPrefs.shaft;
                  const hasPrefs = headPrefs || shaftPrefs;

                  return (
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                          Combo Profile
                          <span title="Blue = this combo's equipment characteristics. Orange = your saved preferences from your profile." className="ml-1 cursor-help text-gray-300">ⓘ</span>
                        </p>
                        {hasPrefs && (
                          <div className="flex items-center gap-2 text-[10px] text-gray-400">
                            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-blue-500" />Recommended</span>
                            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-orange-400" />Your prefs</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 justify-center">
                        <div className="text-center">
                          <p className="text-[10px] text-gray-400 mb-1">Head</p>
                          <RadarChartComponent
                            axes={activeHeadAxes}
                            datasets={[
                              { name: 'Recommended', color: '#3B82F6', fillOpacity: 0.2, values: shaft.combo_radar.head_radar },
                              ...(headPrefs ? [{ name: 'Your prefs', color: '#f97316', fillOpacity: 0.15, values: headPrefs }] : []),
                            ]}
                            size="sm"
                            showLegend={false}
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-gray-400 mb-1">Shaft</p>
                          <RadarChartComponent
                            axes={activeShaftAxes}
                            datasets={[
                              { name: 'Recommended', color: '#10B981', fillOpacity: 0.2, values: shaft.combo_radar.shaft_radar },
                              ...(shaftPrefs ? [{ name: 'Your prefs', color: '#f97316', fillOpacity: 0.15, values: shaftPrefs }] : []),
                            ]}
                            size="sm"
                            showLegend={false}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={`max-w-4xl mx-auto ${className}`}>
        <div className="bg-white p-12 rounded-lg shadow-sm border text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-ink mb-4"></div>
          <h3 className="text-xl font-semibold text-gray-700">Analyzing Equipment Database</h3>
          <p className="text-gray-500 mt-2">Finding your perfect matches...</p>
        </div>
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={`max-w-4xl mx-auto ${className}`}>
        <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Recommendations</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              {onBack && (
                <div className="mt-4">
                  <button
                    onClick={onBack}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 font-medium text-sm"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── No data state ────────────────────────────────────────────────────────
  if (!data || !data.heads || data.heads.length === 0) {
    return (
      <div className={`max-w-4xl mx-auto ${className}`}>
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">No Recommendations Found</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>We couldn't find any equipment matching your profile. Please try adjusting your criteria.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <div className={`max-w-4xl mx-auto ${className} ${selectedHeadIds.size > 0 ? 'pb-20' : ''}`}>

      {/* Header Section */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            {viewMode === 'selected' ? (
              <>
                <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Comparing Selected Heads</h1>
                <p className="text-gray-600 mt-1">
                  {selectedHeadIds.size} head{selectedHeadIds.size !== 1 ? 's' : ''} selected — showing all {data.metadata.shafts_per_head} shaft options each
                </p>
              </>
            ) : (
              <>
                <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Your Equipment Recommendations</h1>
                <p className="text-gray-600 mt-1">
                  {data.heads.length} {data.equipment_type} heads ranked by fit
                </p>
              </>
            )}

            {/* Scoring Mode Badge — one name per mode, matching QuickFitPage's vocabulary (W4) */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                data.metadata.scoring_mode === 'constraint_first'
                  ? 'bg-cyan/15 text-cyan-ink'
                  : data.metadata.scoring_mode === 'rule_based'
                    ? 'bg-line text-ink-2'
                    : 'bg-green/15 text-green-ink'
              }`}>
                {data.metadata.scoring_mode === 'constraint_first'
                  ? 'V9 Physics + AI'
                  : data.metadata.scoring_mode === 'rule_based'
                    ? 'Legacy Rule-Based'
                    : 'ML Model'}
              </span>
              {data.metadata.scoring_version && (
                <span className="text-xs text-gray-500">{data.metadata.scoring_version}</span>
              )}
              {data.metadata.total_combos_evaluated && (
                <span className="text-xs text-gray-400">
                  ({data.metadata.total_combos_evaluated.toLocaleString()} combos evaluated)
                </span>
              )}
            </div>

            {/* Shaft availability filter — separated from the badges above so it doesn't read as
                another status pill; label + title text explain what clicking one does. */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="text-xs text-gray-500">Show only shafts that are:</span>
              {([
                ['stock', 'Stock', 'Shows only shafts that come standard on the head at no extra cost.'],
                ['stock_alternate', 'Stock Alternate', 'Shows only shafts offered as a no-upcharge stock option (not the default, but no extra cost).'],
                ['small_upcharge', 'Small Upcharge', `Shows only shafts available for a small upcharge (up to $${data?.equipment_type === 'iron' ? '20' : '200'}).`],
              ] as const).map(([mode, label, title]) => (
                <button
                  key={mode}
                  type="button"
                  title={title}
                  onClick={() => setShaftFilterMode(m => (m === mode ? 'all' : mode))}
                  aria-pressed={shaftFilterMode === mode}
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                    shaftFilterMode === mode
                      ? 'bg-green/15 text-green-ink border-green-ink'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {shaftFilterMode === mode ? `✓ ${label}` : label}
                </button>
              ))}
              {shaftFilterMode !== 'all' && (
                <button
                  type="button"
                  onClick={() => setShaftFilterMode('all')}
                  className="text-xs text-gray-400 hover:text-gray-600 underline"
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>

          {/* Back button (selected mode) */}
          {viewMode === 'selected' && (
            <button
              onClick={handleBackToAll}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 bg-white border border-gray-300 rounded-md px-3 py-1.5 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              All Heads
            </button>
          )}
        </div>

        {/* Info Banner */}
        {viewMode === 'all' && (
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-800 font-medium">
                  <span className="font-bold">Step 1</span> — Select 1–5 heads from the tiers below.{' '}
                  <span className="font-bold">Step 2</span> — Click "View Shaft Options" to see all {data.metadata.shafts_per_head} shaft options for your selected heads.
                </p>
                <p className="hidden sm:block text-xs text-blue-600 mt-1">
                  Each shaft card shows a brief explanation. Click "Details" to expand technical score breakdowns.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── F1: Unified "problems → delivery" narrative — one block, top pick ── */}
      {viewMode === 'all' && data.heads.length > 0 && (() => {
        const topHead = data.heads[0];
        const topNeeds = (data.player_needs?.needs ?? [])
          .filter(n => n.priority !== 'LOW')
          .slice(0, 2);
        const traj = topHead.trajectory;
        const deliveryParts: string[] = [];
        if (traj?.carry_gain_yds != null) {
          deliveryParts.push(`${traj.carry_gain_yds >= 0 ? '+' : ''}${traj.carry_gain_yds.toFixed(1)} yds carry`);
        }
        if ((traj?.consistency_gain_yds ?? 0) > 0) {
          deliveryParts.push(`+${traj!.consistency_gain_yds!.toFixed(1)} yds steadier on mishits`);
        }
        if (topNeeds.length === 0 && deliveryParts.length === 0) return null;

        return (
          <div className="mb-4 border border-green-ink bg-green/10 rounded-lg p-4">
            <p className="text-xs font-semibold text-green-ink uppercase tracking-wide mb-1">Best club for you</p>
            <p className="text-base font-bold text-gray-900">
              {topHead.head_brand} {topHead.head_model}{topHead.head_year ? ` (${topHead.head_year})` : ''}
            </p>
            {topNeeds.length > 0 && (
              <p className="text-sm text-gray-700 mt-2">
                <span className="font-medium">Your problems: </span>
                {topNeeds.map(n => n.human_label).join('; ')}
              </p>
            )}
            {deliveryParts.length > 0 && (
              <p className="text-sm text-green-ink mt-1">
                <span className="font-medium">This club delivers: </span>
                {deliveryParts.join(' · ')} vs your current setup
              </p>
            )}
          </div>
        );
      })()}

      {/* ── Your Fitting Analysis (V9 only) ──────────────────────────────── */}
      {data.player_needs && (() => {
        const actionNeeds = data.player_needs!.needs.filter(n => n.priority !== 'LOW');
        if (actionNeeds.length === 0) return null;
        const displayNeeds = actionNeeds.slice(0, 3);
        return (
          <div className="mb-4 border border-indigo-200 rounded-lg overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-4 py-3 bg-indigo-50 hover:bg-indigo-100 transition-colors text-left"
              onClick={() => setFittingAnalysisOpen(o => !o)}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-indigo-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="text-sm font-semibold text-indigo-900">Your Fitting Analysis</span>
                <span className="text-xs text-indigo-500 font-normal">{displayNeeds.length} key finding{displayNeeds.length !== 1 ? 's' : ''}</span>
              </div>
              <svg className={`w-4 h-4 text-indigo-500 transition-transform ${fittingAnalysisOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {fittingAnalysisOpen && (
              <div className="divide-y divide-indigo-100 bg-white">
                {displayNeeds.map((need, i) => (
                  <div key={i} className="px-4 py-3 flex items-start gap-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold shrink-0 mt-0.5 ${
                      need.priority === 'HIGH'
                        ? 'bg-red-100 text-red-700 border border-red-200'
                        : 'bg-amber-100 text-amber-700 border border-amber-200'
                    }`}>
                      {need.priority}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{need.human_label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{need.equipment_hint}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Spin Diagnosis Banners ─────────────────────────────────────────── */}
      {(() => {
        const driverDiag = data.driver_diagnosis;
        const ironDiag = data.heads?.[0]?.shafts?.[0]?.iron_diagnosis;
        const diag = driverDiag ?? ironDiag;
        if (!diag || !diag.culprit_summary) return null;

        const isDriver = !!driverDiag;
        const spinProblem = isDriver ? (driverDiag as DriverDiagnosis).spin_problem : null;
        const isOk = spinProblem === 'ok';
        const isHigh = spinProblem === 'high';

        const bannerBg = isOk
          ? 'bg-green/10 border-green-ink'
          : isHigh
            ? 'bg-amber-50 border-amber-200'
            : spinProblem === 'low'
              ? 'bg-blue-50 border-blue-200'
              : 'bg-yellow-50 border-yellow-200';

        const iconColor = isOk ? 'text-green-ink' : isHigh ? 'text-amber-500' : spinProblem === 'low' ? 'text-blue-500' : 'text-yellow-500';
        const textColor = isOk ? 'text-green-ink' : isHigh ? 'text-amber-800' : spinProblem === 'low' ? 'text-blue-800' : 'text-yellow-800';
        const subColor = isOk ? 'text-green-ink' : isHigh ? 'text-amber-600' : spinProblem === 'low' ? 'text-blue-600' : 'text-yellow-600';

        return (
          <div className={`border ${bannerBg} rounded-lg p-3 mb-4`}>
            <div className="flex items-start gap-2">
              <svg className={`w-4 h-4 mt-0.5 shrink-0 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
                {isOk
                  ? <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  : <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                }
              </svg>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${textColor}`}>{diag.culprit_summary}</p>
                {isDriver && driverDiag && (
                  <p className={`text-xs mt-1 ${subColor}`}>
                    Measured {driverDiag.measured_spin.toLocaleString()} rpm · Optimal {driverDiag.optimal_spin.toLocaleString()} rpm
                    {driverDiag.equip_fixable_rpm !== 0 && ` · Equipment can fix ~${Math.abs(driverDiag.equip_fixable_rpm).toLocaleString()} rpm`}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Current Driver Benchmark (V9 trajectory) ─────────────────────── */}
      {data.equipment_type === 'driver' && data.metadata.current_driver_benchmark && (() => {
        const bm = data.metadata.current_driver_benchmark!;
        const status = bm.status ?? 'room_to_improve';
        const style = {
          already_well_fit: { bg: 'bg-green/10 border-green-ink', text: 'text-green-ink', sub: 'text-green-ink' },
          upgrade_likely:   { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-800', sub: 'text-amber-600' },
          no_clear_upgrade: { bg: 'bg-gray-50 border-gray-200',   text: 'text-gray-700',  sub: 'text-gray-500'  },
          room_to_improve:  { bg: 'bg-blue-50 border-blue-200',   text: 'text-blue-800',  sub: 'text-blue-600'  },
        }[status];
        const clubName = [bm.brand, bm.model].filter(Boolean).join(' ') || 'current driver';
        const margin = bm.top_candidate_margin_yds;
        const headline = {
          already_well_fit: `Your ${clubName} is already well fit — top picks are within the noise band.`,
          upgrade_likely:   `Upgrade likely — our top pick projects ${margin != null && margin > 0 ? `+${margin.toFixed(1)} yds` : 'a gain'} over your ${clubName}.`,
          no_clear_upgrade: `No clear distance upgrade over your ${clubName}.`,
          room_to_improve:  `Your ${clubName} trajectory has room to improve.`,
        }[status];

        return (
          <div className={`border ${style.bg} rounded-lg p-3 mb-4`}>
            <div className="flex items-start gap-2">
              <svg className={`w-4 h-4 mt-0.5 shrink-0 ${style.sub}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${style.text}`}>{headline}</p>
                <p className={`text-xs mt-1 ${style.sub}`}>
                  {bm.within_optimal_yds != null && `Current trajectory is ${bm.within_optimal_yds.toFixed(1)} yds off optimal`}
                  {bm.estimated_mishit_loss_yds != null && ` · est. mishit cost ~${bm.estimated_mishit_loss_yds.toFixed(0)} yds`}
                  {bm.current_head_year != null && ` · ${bm.current_head_year} head`}
                  {bm.current_head_confidence === 'low' && ' · specs estimated from model year (low confidence)'}
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── View tabs: Best Fit | Approach Paths | Radar Match ── */}
      {viewMode === 'all' && (data.approaches?.length || showRadarMatchTab) && (
        <div className="mb-4">
          <div className="flex flex-wrap rounded-lg border border-gray-200 bg-gray-50 p-1 gap-1 w-fit max-w-full">
            {/* Best Fit tab */}
            <button
              onClick={() => { setActiveApproachIdx(null); setSortMode('bestfit'); }}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeApproachIdx === null && sortMode !== 'radarMatch'
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Best Fit
            </button>

            {/* Approach path tabs */}
            {data.approaches?.map((ap, i) => (
              <button
                key={i}
                onClick={() => { setActiveApproachIdx(i); setSortMode('bestfit'); }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors truncate max-w-[160px] ${
                  activeApproachIdx === i
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title={ap.label}
              >
                {ap.label}
              </button>
            ))}

            {/* Radar Match tab */}
            {showRadarMatchTab && (
              <button
                onClick={() => { setActiveApproachIdx(null); setSortMode('radarMatch'); }}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeApproachIdx === null && sortMode === 'radarMatch'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
                Radar Match
                {!radarPrefs && (
                  <span className="text-[10px] text-amber-500 font-normal">(no prefs set)</span>
                )}
              </button>
            )}
          </div>

          {/* Approach path rationale banner */}
          {activeApproachIdx !== null && data.approaches?.[activeApproachIdx] && (
            <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
              <p className="text-xs text-blue-800">{data.approaches[activeApproachIdx].rationale}</p>
            </div>
          )}

          {/* Radar Match info banner */}
          {activeApproachIdx === null && sortMode === 'radarMatch' && (
            <div className="mt-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2.5">
              {radarPrefs ? (
                <p className="text-xs text-purple-800">
                  <span className="font-semibold">All shafts shown are a verified fit for your swing.</span>{' '}
                  This view re-ranks them using your radar preferences — giving you the best balance between
                  technical fit and personal feel. Your swing-data ranking is always one tap away under <span className="font-medium">Best Fit</span>.
                </p>
              ) : (
                <p className="text-xs text-purple-700">
                  Set your radar preferences on the profile page to enable preference-weighted ranking.
                  Showing best-fit order for now.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Approach Path View ── */}
      {viewMode === 'all' && activeApproachIdx !== null && data.approaches?.[activeApproachIdx] && (() => {
        const approach = data.approaches![activeApproachIdx];
        if (!approach.top_combos?.length) {
          return (
            <p className="text-sm text-gray-500 text-center py-8">No combos available for this path.</p>
          );
        }
        return (
          <div className="space-y-3">
            {approach.top_combos.map((combo, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-white border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white font-bold text-sm">{i + 1}</span>
                      <div>
                        <h3 className="font-bold text-gray-900 text-sm">{combo.head.head_brand} {combo.head.head_model}</h3>
                        <p className="text-xs text-gray-500">{combo.head.head_loft}° · Head score {combo.head.head_score.toFixed(1)}</p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {combo.combined_score.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="px-4 py-3 flex items-center gap-3">
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {combo.shaft.shaft_brand} {combo.shaft.shaft_model}
                    </p>
                    <p className="text-xs text-gray-500">
                      {combo.shaft.shaft_flex} · {combo.shaft.shaft_weight}g · Shaft score {combo.shaft.shaft_score.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Tiered Head Picker (all mode only, Best Fit tab) ── */}
      {viewMode === 'all' && activeApproachIdx === null && (() => {
        const bestScore = orderedHeads.length > 0 ? Math.max(...orderedHeads.map(h => h.head_score)) : 9.0;
        const tierGroups: Record<TierNumber, HeadRecommendation[]> = { 1: [], 2: [], 3: [], 4: [] };
        orderedHeads.forEach(h => tierGroups[classifyTier(h.head_score, bestScore)].push(h));

        const renderHeadCard = (head: HeadRecommendation, globalRank: number) => {
          const isSelected = selectedHeadIds.has(head.head_id);
          const isSelectionFull = selectedHeadIds.size >= MAX_SELECTED && !isSelected;
          const tier = classifyTier(head.head_score, bestScore);
          const meta = getTierMeta(tier);
          const firstShaftExplanation = head.shafts[0]?.explanation || '';
          const { headSentence } = splitExplanation(firstShaftExplanation);
          const keywordTags = head.head_tags ?? [];

          return (
            <div key={head.head_id} className={`bg-white rounded-lg shadow-sm border-2 transition-all ${
              isSelected ? 'border-green-ink ring-2 ring-green-ink' : meta.border
            }`}>
              <div className={`px-4 py-3 ${isSelected ? 'bg-green/10' : meta.headerBg}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-white font-bold text-sm ${
                        isSelected ? 'bg-green/100' : tier === 1 ? 'bg-amber-500' : tier === 2 ? 'bg-green-ink' : 'bg-gray-400'
                      }`}>{globalRank}</span>
                      <h2 className="text-base font-bold text-gray-900">{head.head_brand} {head.head_model}</h2>
                      {head.head_year && <span className="text-xs text-gray-400">{head.head_year}</span>}
                    </div>
                    <p className="text-sm text-gray-600 mt-1 ml-9">
                      Loft: <span className="font-semibold">{head.head_loft}°</span>
                      {' • '}
                      Fit Score: <span className="font-semibold">{head.head_score.toFixed(1)}/10</span>
                    </p>
                    {head.trajectory && (head.trajectory.carry_gain_yds != null || (head.trajectory.consistency_gain_yds ?? 0) > 0) && (
                      <p className="text-xs mt-0.5 ml-9">
                        {head.trajectory.carry_gain_yds != null && (
                          <span className={head.trajectory.carry_gain_yds >= 0 ? 'text-green-ink' : 'text-gray-400'}>
                            {head.trajectory.carry_gain_yds >= 0 ? '+' : ''}{head.trajectory.carry_gain_yds.toFixed(1)} yds carry vs current
                          </span>
                        )}
                        {(head.trajectory.consistency_gain_yds ?? 0) > 0 && (
                          <span className="text-sky-600">
                            {head.trajectory.carry_gain_yds != null && ' · '}
                            +{head.trajectory.consistency_gain_yds!.toFixed(1)} yds steadier on mishits
                          </span>
                        )}
                      </p>
                    )}
                    {keywordTags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2 ml-9">
                        {keywordTags.map(tag => (
                          <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">{tag}</span>
                        ))}
                      </div>
                    )}
                    {headSentence && (
                      <p className="text-xs text-gray-500 mt-1 ml-9 italic">{headSentence}</p>
                    )}
                  </div>
                  <button
                    onClick={() => toggleHeadSelection(head.head_id)}
                    disabled={isSelectionFull}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                      isSelected
                        ? 'bg-green/100 text-white border-green-ink hover:opacity-90'
                        : isSelectionFull
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {isSelected ? '✓ Selected' : 'Select'}
                  </button>
                </div>
              </div>
            </div>
          );
        };

        let globalRankCounter = 0;

        return (
          <div className="space-y-6">
            {tierGroups[1].length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                    Tier 1 — Best for You ({tierGroups[1].length})
                  </span>
                  <span className="text-xs text-gray-400">Highest fit + latest tech</span>
                </div>
                <div className="space-y-3">
                  {tierGroups[1].map(h => renderHeadCard(h, ++globalRankCounter))}
                </div>
              </div>
            )}

            {tierGroups[2].length > 0 && (() => {
              const latestYear = Math.max(0, ...orderedHeads.map(h => h.head_year ?? 0));
              const OLDER_CUTOFF = latestYear > 0 ? latestYear - 3 : new Date().getFullYear() - 3;
              const newer = tierGroups[2].filter(h => !h.head_year || h.head_year > OLDER_CUTOFF);
              const older = tierGroups[2].filter(h => h.head_year && h.head_year <= OLDER_CUTOFF);
              const newerToShow = newer.slice(0, 7);
              // Show at least 3 older models (padding with extras if fewer than 3 newer shown)
              const olderToShow = older.slice(0, Math.max(3, 10 - newerToShow.length));
              return (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green/15 text-green-ink border border-green-ink">
                      Tier 2 — Good Match ({tierGroups[2].length})
                    </span>
                    <span className="text-xs text-gray-400">Strong fit across swing profile</span>
                  </div>
                  <div className="space-y-3">
                    {newerToShow.map(h => renderHeadCard(h, ++globalRankCounter))}
                  </div>
                  {olderToShow.length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">On a budget?</span>
                        <span className="text-xs text-gray-400">These older models still fit your swing well</span>
                      </div>
                      <div className="space-y-3">
                        {olderToShow.map(h => renderHeadCard(h, ++globalRankCounter))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {tierGroups[3].length > 0 && (
              <div>
                <button
                  onClick={() => setTier3Open(o => !o)}
                  className="flex items-center gap-2 mb-3 group"
                >
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-300">
                    Tier 3 — Acceptable ({tierGroups[3].length})
                  </span>
                  <span className="text-xs text-gray-400 group-hover:text-gray-600">
                    {tier3Open ? '▲ Hide' : '▼ Show'}
                  </span>
                </button>
                {tier3Open && (
                  <div className="space-y-3">
                    {tierGroups[3].slice(0, 5).map(h => renderHeadCard(h, ++globalRankCounter))}
                  </div>
                )}
              </div>
            )}

            {tierGroups[4].length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Not Suited ({tierGroups[4].length} heads excluded)
                </p>
                <div className="space-y-1">
                  {tierGroups[4].map(h => (
                    <p key={h.head_id} className="text-xs text-gray-500">
                      <span className="font-medium text-gray-700">{h.head_brand} {h.head_model}</span>
                      {h.head_year && ` (${h.head_year})`}
                      {' — '}{getTier4Reason(h)}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Full Head+Shaft List (selected mode only — unchanged) ── */}
      {viewMode === 'selected' && (
      <div className="space-y-4">
        {headsToShow.map((head, headIndex) => {
          const isSelected = selectedHeadIds.has(head.head_id);
          const isSelectionFull = selectedHeadIds.size >= MAX_SELECTED && !isSelected;
          const displayedShafts = getShaftsToShow(head);

          // Global rank — based on best-fit order
          const globalRank = orderedHeads.findIndex(h => h.head_id === head.head_id) + 1;

          // Head-level explanation (sentence 1 from first shaft, shown once)
          const firstShaftExplanation = head.shafts[0]?.explanation || '';
          const { headSentence } = splitExplanation(firstShaftExplanation);
          const keywordTags = head.head_tags ?? [];

          // All heads start collapsed (keywords); user can expand with Show more
          const isFirstHead = globalRank === 1;
          const isExplanationCollapsed = !collapsedExplanations.has(head.head_id);

          return (
            <div
              key={head.head_id}
              className={`bg-white rounded-lg shadow-sm border transition-all ${
                isSelected
                  ? 'border-green-ink ring-2 ring-green-ink'
                  : isFirstHead
                  ? 'border-2 border-amber-400'
                  : globalRank > 3
                  ? 'border-gray-100 opacity-90'
                  : 'border-gray-200'
              }`}
            >
              {/* Head Header */}
              <div className={`px-4 py-3 border-b ${isSelected ? 'bg-green/10 border-green-ink' : 'bg-green/10 border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white font-bold text-sm ${isSelected ? 'bg-green/100' : isFirstHead ? 'bg-amber-500' : 'bg-green-ink'}`}>
                        {globalRank}
                      </span>
                      {isFirstHead && !isSelected && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                          Best Match
                        </span>
                      )}
                      <h2 className="text-base sm:text-xl font-bold text-gray-900">
                        {head.head_brand} {head.head_model}
                      </h2>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 ml-10">
                      Loft: <span className="font-semibold">{head.head_loft}°</span>
                      {' • '}
                      Match Score: <span className="font-semibold">{head.head_score.toFixed(1)}/10</span>
                    </p>
                  </div>

                  {/* Select toggle button */}
                  <button
                    onClick={() => toggleHeadSelection(head.head_id)}
                    disabled={isSelectionFull}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                      isSelected
                        ? 'bg-green-ink text-white border-green-ink hover:opacity-90'
                        : isSelectionFull
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-green-ink hover:text-green-ink hover:bg-green/10'
                    }`}
                    title={isSelectionFull ? `Max ${MAX_SELECTED} heads selected` : undefined}
                  >
                    {isSelected ? (
                      <>
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Selected
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Select to Compare
                      </>
                    )}
                  </button>
                </div>

                {/* Head-level explanation (shown once, not repeated per shaft) */}
                {headSentence && (
                  <div className="mt-3 ml-10">
                    {isExplanationCollapsed ? (
                      /* Collapsed: show keyword tags */
                      <button
                        onClick={() => toggleExplanationCollapse(head.head_id)}
                        className="flex items-center gap-1.5 flex-wrap"
                      >
                        {keywordTags.length > 0 ? (
                          keywordTags.map((tag, i) => (
                            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green/10 text-green-ink border border-green-ink">
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-500 line-clamp-1">{headSentence.replace(/\.$/, '')}</span>
                        )}
                        <span className="text-xs text-gray-400 hover:text-gray-600 ml-1">Show more</span>
                      </button>
                    ) : (
                      /* Expanded: full head explanation */
                      <div>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {highlightText(headSentence.replace(/\.$/, ''))}
                        </p>
                        <button
                          onClick={() => toggleExplanationCollapse(head.head_id)}
                          className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                        >
                          Show less
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Shaft Options */}
              <div className="px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  {viewMode === 'selected'
                    ? `All ${displayedShafts.length} Shaft Options`
                    : 'Top Shaft Pairings'}
                </h3>

                {/* Stock Shaft Option — omitted when the OEM mapping is unknown, never fabricated */}
                {head.stock_shaft && (
                  <div className="mb-3 border-2 border-green-ink rounded-lg p-3 bg-green/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-green-ink shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {head.stock_shaft.description}
                          </p>
                          <p className="text-sm text-gray-600">
                            Flex: <span className="font-medium">{head.stock_shaft.flex}</span>
                            {head.stock_shaft.weight != null && (
                              <>
                                {' • '}
                                Weight: <span className="font-medium">{head.stock_shaft.weight}g</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-0.5">
                        {head.stock_shaft.fit_tier ? (
                          <>
                            <span className="text-xs font-medium text-green-ink bg-green/15 px-2 py-1 rounded">
                              {head.stock_shaft.fit_tier === 'stock_standard' ? 'BASELINE' : 'ALTERNATE'}
                            </span>
                            <span className="text-[10px] text-green-ink">
                              {head.stock_shaft.fit_tier === 'stock_standard' ? 'Comes standard' : 'Stock option'}
                            </span>
                            <span className="text-[10px] font-medium text-amber-700">
                              {head.stock_shaft.upcharge_usd == null
                                ? 'No upcharge on file'
                                : head.stock_shaft.upcharge_usd > 0
                                  ? `+$${head.stock_shaft.upcharge_usd.toFixed(0)} upcharge`
                                  : '$0 upcharge'}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                              ESTIMATED
                            </span>
                            <span className="text-[10px] text-gray-500">
                              No OEM mapping on file
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom Shaft Options — 2-column grid for better readability */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {displayedShafts.map((shaft, shaftIndex) =>
                    renderShaftCard(shaft, head, shaftIndex)
                  )}
                </div>

              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* ── Sticky Selection Bar ── */}
      {selectedHeadIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white z-50 shadow-2xl border-t border-gray-700">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-200 shrink-0">
              <span className="text-white font-bold">{selectedHeadIds.size}</span>
              {' '}head{selectedHeadIds.size !== 1 ? 's' : ''} selected
              <span className="hidden sm:inline text-gray-400 ml-1">
                {selectedHeadIds.size < MAX_SELECTED && `(pick up to ${MAX_SELECTED})`}
              </span>
            </span>

            <div className="flex items-center gap-2 shrink-0">
              {viewMode === 'selected' ? (
                <button
                  onClick={handleBackToAll}
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-200 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-600 px-3 py-1.5 rounded-md transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="hidden sm:inline">Back to All Heads</span>
                  <span className="sm:hidden">Back</span>
                </button>
              ) : (
                <button
                  onClick={handleCompareSelected}
                  className="flex items-center gap-1.5 text-sm font-semibold text-white bg-green-ink hover:bg-green/100 px-3 py-1.5 rounded-md transition-colors"
                >
                  <span className="hidden sm:inline">View Shaft Options ({selectedHeadIds.size} head{selectedHeadIds.size !== 1 ? 's' : ''})</span>
                  <span className="sm:hidden">View Shafts ({selectedHeadIds.size})</span>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              <button
                onClick={handleClearSelection}
                className="text-sm font-medium text-gray-400 hover:text-white px-2 py-1.5 rounded-md transition-colors"
                title="Clear selection"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MLRecommendationResults;
